// Policy pages (snippets/policy-page-2cv.liquid), drawn from the text in
// Settings > Policies.
//
// Tap-to-open sections, one open at a time. The contents list opens the
// section it links to, as do links to a section (#heading) and the
// browser's find-in-page. Policies shown in full on desktop (Theme settings
// > Policy pages) show every section there (CSS), and the contents list
// instead highlights the section being read.

(() => {
  // The Print button in the title band.
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-policy-print]')) window.print();
  });

  class Policy2cv extends HTMLElement {
    connectedCallback() {
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.toc = this.querySelector('.policy-2cv__toc');
      this.tocToggle = this.querySelector('.policy-2cv__toc-toggle');

      this.onClick = this.onClick.bind(this);
      this.onBeforeMatch = this.onBeforeMatch.bind(this);
      this.onHashChange = this.onHashChange.bind(this);
      this.onLayoutChange = this.onLayoutChange.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onBeforePrint = this.onBeforePrint.bind(this);
      this.onAfterPrint = this.onAfterPrint.bind(this);

      this.addEventListener('click', this.onClick);
      this.panels.forEach((panel) => panel.addEventListener('beforematch', this.onBeforeMatch));
      this.desktop.addEventListener('change', this.onLayoutChange);
      window.addEventListener('hashchange', this.onHashChange);
      window.addEventListener('beforeprint', this.onBeforePrint);
      window.addEventListener('afterprint', this.onAfterPrint);

      this.onLayoutChange();
      this.onHashChange();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.panels.forEach((panel) => panel.removeEventListener('beforematch', this.onBeforeMatch));
      this.desktop.removeEventListener('change', this.onLayoutChange);
      window.removeEventListener('hashchange', this.onHashChange);
      window.removeEventListener('beforeprint', this.onBeforePrint);
      window.removeEventListener('afterprint', this.onAfterPrint);
      window.removeEventListener('scroll', this.onScroll);
    }

    get sections() {
      return Array.from(this.querySelectorAll('[data-policy-section]'));
    }

    get panels() {
      return this.querySelectorAll('.policy-2cv__panel');
    }

    isAccordion() {
      return this.dataset.layout !== 'full' || !this.desktop.matches;
    }

    onClick(event) {
      const toggle = event.target.closest('.policy-2cv__toggle');
      if (toggle) {
        const section = toggle.closest('[data-policy-section]');
        this.open(toggle.getAttribute('aria-expanded') === 'true' ? null : section);
        return;
      }

      if (event.target.closest('.policy-2cv__toc-toggle')) {
        this.setTocOpen(this.tocToggle.getAttribute('aria-expanded') !== 'true');
        return;
      }

      // Open the section first, so the browser's jump to it lands in the
      // right place once the contents box and any open section above close.
      const link = event.target.closest('.policy-2cv__toc-link');
      if (link) {
        this.setTocOpen(false);
        const section = this.sectionFor(link.hash);
        if (section && this.isAccordion()) this.open(section);
      }
    }

    onBeforeMatch(event) {
      this.open(event.target.closest('[data-policy-section]'));
    }

    // On load and when the address changes to #a-section: open it, then
    // scroll to it again, as closing a section above it moves it up.
    onHashChange() {
      const section = this.sectionFor(window.location.hash);
      if (!section) return;
      this.open(section);
      section.scrollIntoView({ block: 'start' });
    }

    onLayoutChange() {
      const spying = !this.isAccordion();
      if (spying && !this.spying) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.highlightReading();
      } else if (!spying && this.spying) {
        window.removeEventListener('scroll', this.onScroll);
        this.highlight(this.querySelector('[data-policy-section].is-open'));
      }
      this.spying = spying;
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.highlightReading();
      });
    }

    // Printing shows every section; the print styles do too, this covers
    // browsers that keep "until found" content hidden regardless.
    onBeforePrint() {
      this.panels.forEach((panel) => {
        if (!panel.hidden) return;
        panel.hidden = false;
        panel.dataset.printOpened = '';
      });
    }

    onAfterPrint() {
      this.querySelectorAll('.policy-2cv__panel[data-print-opened]').forEach((panel) => {
        panel.setAttribute('hidden', 'until-found');
        delete panel.dataset.printOpened;
      });
    }

    sectionFor(hash) {
      if (!hash || hash.length < 2) return null;
      let id;
      try {
        id = decodeURIComponent(hash.slice(1));
      } catch {
        return null;
      }
      const section = document.getElementById(id);
      return section && this.contains(section) && section.matches('[data-policy-section]') ? section : null;
    }

    // Opens one section and closes the rest; null closes them all.
    open(target) {
      this.sections.forEach((section) => {
        const isOpen = section === target;
        section.classList.toggle('is-open', isOpen);
        section.querySelector('.policy-2cv__toggle').setAttribute('aria-expanded', isOpen);
        const panel = section.querySelector('.policy-2cv__panel');
        if (isOpen) {
          panel.removeAttribute('hidden');
        } else if (!panel.hidden) {
          panel.setAttribute('hidden', 'until-found');
        }
      });
      if (this.isAccordion()) this.highlight(target);
    }

    setTocOpen(open) {
      if (!this.toc) return;
      this.toc.classList.toggle('is-open', open);
      this.tocToggle.setAttribute('aria-expanded', open);
    }

    highlight(target) {
      this.querySelectorAll('.policy-2cv__toc-link').forEach((link) => {
        link.classList.toggle('is-active', Boolean(target) && link.hash === `#${target.id}`);
      });
    }

    // The section being read: the last one whose top has scrolled past a
    // line a quarter of the way down, below the sticky nav bar.
    highlightReading() {
      const headerHeight =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const line = headerHeight + window.innerHeight * 0.25;
      const sections = this.sections;
      let current = sections[0];
      for (const section of sections) {
        if (section.getBoundingClientRect().top > line) break;
        current = section;
      }
      this.highlight(current);
    }
  }

  if (!customElements.get('policy-2cv')) {
    customElements.define('policy-2cv', Policy2cv);
  }
})();
